import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { effect, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, filter, from, map, shareReplay, switchMap } from 'rxjs';
import { defaultLocations, Language, languages, localeLoaders, locations } from '../consts';
import { buildLocale, defaultLocale } from '../lib/build-locale';
import { getPathType } from '../lib/get-path-type';
import { getPathLanguage, getPathLocation } from '../lib/locale-extract';
import { BrowserService } from './browser.service';
import { GrowthBookService } from './growth-book.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';

const storageLocaleKey = 'preferred-locale';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private browserService = inject(BrowserService);
  private storage = inject(StorageService);
  private state = inject(StateService);
  private growthBookService = inject(GrowthBookService);
  private router = inject(Router);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  private readonly prefLocale$ = new BehaviorSubject<string>(this.storage.get(storageLocaleKey) as string);
  private readonly userLocale$ = this.state.user$.pipe(map(user => user?.locale));

  private getPathType(path: string[]) {
    return getPathType(path, this.browserService.isTenantDomain());
  }

  private readonly path$ = this.router.events.pipe(
    filter(event => event instanceof NavigationStart),
    map(event => event.url.split('/').filter(Boolean)),
    shareReplay(1)
  );

  private readonly pathLocale$ = this.path$.pipe(
    map(path => {
      if (this.browserService.isExtension()) return;
      const type = this.getPathType(path);
      if (type !== 'public') return; // only public paths have locale in URL
      const lang = getPathLanguage(path);
      const location = getPathLocation(path);
      return buildLocale(lang, location);
    }),
    shareReplay(1)
  );

  private readonly pathType$ = this.path$.pipe(
    map(path => {
      if (this.browserService.isExtension()) return;
      return this.getPathType(path);
    }),
    shareReplay(1)
  );

  private pathLocale = toSignal(this.pathLocale$);

  private path = toSignal(this.path$); // needs to be here to ensure that pathLocale$ starts before the first NavigationStart event

  private readonly browserLocale = isPlatformBrowser(this.platformId)
    ? (() => {
        const navigatorLangs = navigator.languages;
        if (!navigatorLangs || navigatorLangs.length === 0) {
          return null;
        }
        for (const locale of navigatorLangs) {
          const [lang, loc] = locale.split('-').map(part => part.toLowerCase()) as [Language, string];
          if (languages.some(l => l.lang === lang)) {
            const languageLocations = locations[lang] as readonly string[];
            if (loc && languageLocations.includes(loc)) {
              return buildLocale(lang, loc);
            }
            return buildLocale(lang, defaultLocations[lang]);
          }
        }
      })()
    : null;

  private readonly targetUserLocale$ = this.growthBookService.loaded$.pipe(
    filter(Boolean),
    switchMap(() => {
      return combineLatest([this.pathLocale$, this.prefLocale$, this.userLocale$]).pipe(
        map(([pathLocale, prefLocale, userLocale]) => {
          const locale = pathLocale || userLocale || prefLocale || this.browserLocale || defaultLocale;
          const lang = locale.split('-')[0] as Language;
          const langData = languages.find(l => l.lang === lang);
          if (langData && !langData.allowAsUserLanguage) return defaultLocale;
          return locale;
        })
      );
    }),
    shareReplay(1)
  );

  public readonly currentLocale$ = combineLatest([
    this.pathLocale$,
    this.prefLocale$,
    this.userLocale$,
    this.pathType$,
  ]).pipe(
    map(([pathLocale, prefLocale, userLocale, pathType]) => {
      const locale = pathLocale || userLocale || prefLocale || this.browserLocale || defaultLocale;
      const lang = locale.split('-')[0] as Language;
      const langData = languages.find(l => l.lang === lang);

      if (langData) {
        if ((pathType === 'public' || pathType === 'auth' || pathLocale) && !langData.i18n.website)
          return defaultLocale;
        if (pathType === 'app' && !langData.i18n.app) return defaultLocale;
        if (pathType === 'tenant-domain' && !langData.i18n.tenantDomain) return defaultLocale;
      }
      return locale;
    })
  );

  public readonly currentLocale = toSignal(this.currentLocale$);

  // target locale should be used carefully. Translations could be missing for this locale.
  public readonly targetUserLocale = toSignal(this.targetUserLocale$);

  public readonly currentLanguage$ = this.currentLocale$.pipe(
    map(locale => locale.split('-')[0] as Language),
    shareReplay(1)
  );
  public readonly currentLocation$ = this.currentLocale$.pipe(
    map(locale => locale.split('-')[1]),
    shareReplay(1)
  );

  public readonly currentLanguage = toSignal(this.currentLanguage$);
  public readonly currentLocation = toSignal(this.currentLocation$);

  constructor() {
    effect(() => {
      const currentLanguage = this.currentLanguage();
      if (!currentLanguage) return;
      // Update HTML lang attribute for SEO and accessibility
      if (this.document?.documentElement) {
        this.document.documentElement.lang = currentLanguage;
      }
    });
    effect(() => {
      const pathLocale = this.pathLocale();
      if (!pathLocale) return;
      // We automaticaly set the preferred locale if there is a locale in the path
      // This ensures that navigating from a localized path to a non-localized path retains the locale
      this.setPreferredLocale(pathLocale);
    });
  }

  public setPreferredLocale(locale: string) {
    this.prefLocale$.next(locale);
    this.storage.set(storageLocaleKey, locale);
  }

  public loadedLocale$ = this.currentLocale$.pipe(
    switchMap(locale => from(this.loadLocale(locale)).pipe(map(() => locale))),
    filter(locale => locale === this.currentLocale()),
    shareReplay(1)
  );

  private async loadLocale(locale: string) {
    return localeLoaders[locale]?.();
  }
}
